import { AudioStepSettingsType } from '../../../dorian-shared/types/audio/AudioStepSettingsType';

export type StepAudioPosition = {
  branchId: number;
  stepIndex: number | null;
};

type StepAudioPlaybackDeps = {
  processAudios: (stepAudios: AudioStepSettingsType | null | undefined) => void;
  stopAllAudio: () => void;
};

export type StepAudioPlayback = {
  deliver: (position: StepAudioPosition, stepAudios: AudioStepSettingsType | undefined) => void;
};

// Position-only dedup silenced audio changes on the same position (audio modal
// OK, Cancel-rollback) — content key separates audio-data edits from other step
// edits (text, character, etc.) that must not restart the track.
function toAudiosKey(stepAudios: AudioStepSettingsType | undefined): string {
  return JSON.stringify(stepAudios ?? null);
}

export function createStepAudioPlayback(deps: StepAudioPlaybackDeps): StepAudioPlayback {
  const { processAudios, stopAllAudio } = deps;

  let lastPosition: StepAudioPosition | null = null;
  let lastAudiosKey: string | null = null;

  function deliver(position: StepAudioPosition, stepAudios: AudioStepSettingsType | undefined): void {
    const prevPosition = lastPosition;
    const prevAudiosKey = lastAudiosKey;
    const audiosKey = toAudiosKey(stepAudios);

    lastPosition = position;
    lastAudiosKey = audiosKey;

    const positionChanged = prevPosition === null
      || prevPosition.branchId !== position.branchId
      || prevPosition.stepIndex !== position.stepIndex;

    if (positionChanged) {
      if (prevPosition !== null && prevPosition.branchId !== position.branchId) {
        stopAllAudio();
      }
      if (stepAudios && stepAudios.length > 0) {
        processAudios(stepAudios);
      }
      return;
    }

    if (audiosKey === prevAudiosKey) {
      // No-op: text/character/other edits on the same step must not restart the track.
      return;
    }

    stopAllAudio();
    if (stepAudios && stepAudios.length > 0) {
      processAudios(stepAudios);
    }
  }

  return { deliver };
}
